/**
 * Tour step definitions for the restaurant onboarding wizard.
 *
 * Each section has:
 *  - id: unique identifier
 *  - route: default dashboard route for that section
 *  - tKey: i18n prefix for the section title
 *  - steps[]: array of individual steps
 *
 * Each step has:
 *  - target: data-tour attribute value (matched via [data-tour="..."]) or null for centered modal
 *  - titleKey / contentKey: i18n keys (appended to "dashboard.tour.")
 *  - placement: preferred tooltip position (top | bottom | left | right | center)
 *  - route: (optional) override the section route for this specific step
 */

export const TOUR_SECTIONS = [
  // 0 – Welcome
  {
    id: "welcome",
    route: "",
    tKey: "dashboard.tour.sections.welcome",
    steps: [
      {
        target: null,
        titleKey: "welcome.step1.title",
        contentKey: "welcome.step1.content",
        placement: "center",
      },
    ],
  },

  // 1 – Overview
  {
    id: "overview",
    route: "",
    tKey: "dashboard.tour.sections.overview",
    steps: [
      {
        target: "overview-kpi-row",
        titleKey: "overview.step1.title",
        contentKey: "overview.step1.content",
        placement: "bottom",
      },
      {
        target: "overview-sales-card",
        titleKey: "overview.step2.title",
        contentKey: "overview.step2.content",
        placement: "top",
      },
      {
        target: "overview-bookings-card",
        titleKey: "overview.step3.title",
        contentKey: "overview.step3.content",
        placement: "top",
      },
      {
        target: "overview-items-card",
        titleKey: "overview.step4.title",
        contentKey: "overview.step4.content",
        placement: "top",
      },
      {
        target: "overview-customers-card",
        titleKey: "overview.step5.title",
        contentKey: "overview.step5.content",
        placement: "top",
      },
      {
        target: "overview-ratings-card",
        titleKey: "overview.step6.title",
        contentKey: "overview.step6.content",
        placement: "top",
      },
      {
        target: "overview-partners-card",
        titleKey: "overview.step7.title",
        contentKey: "overview.step7.content",
        placement: "top",
      },
    ],
  },

  // 2 – Banking / Stripe
  {
    id: "banking",
    route: "",
    tKey: "dashboard.tour.sections.banking",
    steps: [
      {
        target: "stripe-button",
        titleKey: "banking.step1.title",
        contentKey: "banking.step1.content",
        placement: "bottom",
      },
      {
        target: null,
        titleKey: "banking.step2.title",
        contentKey: "banking.step2.content",
        placement: "center",
      },
    ],
  },

  // 3 – AI Import
  {
    id: "ai-import",
    route: "products",
    tKey: "dashboard.tour.sections.aiImport",
    steps: [
      {
        target: "import-menu-btn",
        titleKey: "aiImport.step1.title",
        contentKey: "aiImport.step1.content",
        placement: "bottom",
      },
      {
        target: "import-menu-modal",
        titleKey: "aiImport.step2.title",
        contentKey: "aiImport.step2.content",
        placement: "center",
      },
      {
        target: null,
        titleKey: "aiImport.step3.title",
        contentKey: "aiImport.step3.content",
        placement: "center",
      },
      {
        target: null,
        titleKey: "aiImport.step4.title",
        contentKey: "aiImport.step4.content",
        placement: "center",
      },
    ],
  },

  // 4 – Schedules
  {
    id: "schedules",
    route: "settings",
    tKey: "dashboard.tour.sections.schedules",
    steps: [
      {
        target: "settings-tabs",
        titleKey: "schedules.step1.title",
        contentKey: "schedules.step1.content",
        placement: "bottom",
      },
      {
        target: null,
        titleKey: "schedules.step2.title",
        contentKey: "schedules.step2.content",
        placement: "center",
      },
    ],
  },

  // 5 – First Dish
  {
    id: "first-dish",
    route: "products",
    tKey: "dashboard.tour.sections.firstDish",
    steps: [
      {
        target: null,
        titleKey: "firstDish.step1.title",
        contentKey: "firstDish.step1.content",
        placement: "center",
      },
      {
        target: "add-dish-btn",
        titleKey: "firstDish.step2.title",
        contentKey: "firstDish.step2.content",
        placement: "bottom",
      },
      {
        target: "dish-form-title-description",
        titleKey: "firstDish.step3.title",
        contentKey: "firstDish.step3.content",
        placement: "right",
        route: "products/add",
      },
      {
        target: "dish-form-image",
        titleKey: "firstDish.step4.title",
        contentKey: "firstDish.step4.content",
        placement: "right",
        route: "products/add",
      },
      {
        target: "dish-form-dietary",
        titleKey: "firstDish.step5.title",
        contentKey: "firstDish.step5.content",
        placement: "right",
        route: "products/add",
      },
      {
        target: "dish-form-type",
        titleKey: "firstDish.step6.title",
        contentKey: "firstDish.step6.content",
        placement: "right",
        route: "products/add",
      },
      {
        target: "dish-form-variants",
        titleKey: "firstDish.step7.title",
        contentKey: "firstDish.step7.content",
        placement: "right",
        route: "products/add",
      },
      {
        target: "dish-form-price",
        titleKey: "firstDish.step8.title",
        contentKey: "firstDish.step8.content",
        placement: "right",
        route: "products/add",
      },
      {
        target: "dish-publish-buttons",
        titleKey: "firstDish.step9.title",
        contentKey: "firstDish.step9.content",
        placement: "left",
        route: "products/add",
      },
      {
        target: "products-search",
        titleKey: "firstDish.step10.title",
        contentKey: "firstDish.step10.content",
        placement: "bottom",
        route: "products",
      },
      {
        target: "products-table",
        titleKey: "firstDish.step11.title",
        contentKey: "firstDish.step11.content",
        placement: "top",
        route: "products",
      },
    ],
  },

  // 6 – Menu Creation
  {
    id: "menus",
    route: "menus",
    tKey: "dashboard.tour.sections.menus",
    steps: [
      {
        target: null,
        titleKey: "menus.step1.title",
        contentKey: "menus.step1.content",
        placement: "center",
      },
      {
        target: "add-menu-btn",
        titleKey: "menus.step2.title",
        contentKey: "menus.step2.content",
        placement: "bottom",
      },
      {
        target: "menu-form-title-description",
        titleKey: "menus.step3.title",
        contentKey: "menus.step3.content",
        placement: "right",
        route: "menus/add",
      },
      {
        target: "menu-form-dishes",
        titleKey: "menus.step4.title",
        contentKey: "menus.step4.content",
        placement: "right",
        route: "menus/add",
      },
      {
        target: null,
        titleKey: "menus.step5.title",
        contentKey: "menus.step5.content",
        placement: "center",
        route: "menus/add",
      },
      {
        target: "menu-form-price",
        titleKey: "menus.step6.title",
        contentKey: "menus.step6.content",
        placement: "right",
        route: "menus/add",
      },
    ],
  },

  // 7 – Today's Special
  {
    id: "todays-special",
    route: "todaysspecials",
    tKey: "dashboard.tour.sections.todaysSpecial",
    steps: [
      {
        target: null,
        titleKey: "todaysSpecial.step1.title",
        contentKey: "todaysSpecial.step1.content",
        placement: "center",
      },
      {
        target: "add-special-btn",
        titleKey: "todaysSpecial.step2.title",
        contentKey: "todaysSpecial.step2.content",
        placement: "bottom",
      },
      {
        target: "special-form-title-description",
        titleKey: "todaysSpecial.step3.title",
        contentKey: "todaysSpecial.step3.content",
        placement: "right",
        route: "todaysspecials/add",
      },
      {
        target: "special-form-dishes",
        titleKey: "todaysSpecial.step4.title",
        contentKey: "todaysSpecial.step4.content",
        placement: "right",
        route: "todaysspecials/add",
      },
      {
        target: "special-form-dates",
        titleKey: "todaysSpecial.step5.title",
        contentKey: "todaysSpecial.step5.content",
        placement: "right",
        route: "todaysspecials/add",
      },
      {
        target: "special-form-recurring",
        titleKey: "todaysSpecial.step6.title",
        contentKey: "todaysSpecial.step6.content",
        placement: "right",
        route: "todaysspecials/add",
      },
      {
        target: null,
        titleKey: "todaysSpecial.step7.title",
        contentKey: "todaysSpecial.step7.content",
        placement: "center",
        route: "todaysspecials/add",
      },
    ],
  },

  // 8 – Ecosystem
  {
    id: "modules",
    route: "modules",
    tKey: "dashboard.tour.sections.modules",
    steps: [
      {
        target: "modules-list",
        titleKey: "modules.step1.title",
        contentKey: "modules.step1.content",
        placement: "top",
      },
      {
        target: null,
        titleKey: "modules.step2.title",
        contentKey: "modules.step2.content",
        placement: "center",
      },
    ],
  },

];
